import React from "react";
// @ts-ignore
import { NotificationManager } from "react-notifications";

// HOW LONG A TOAST STAYS UP.
//
// Anything that went RIGHT can go away on its own — the person already knows
// what they did, and the toast is a receipt. Anything that went WRONG has to be
// read, and often written down or forwarded, so it waits to be dismissed
// (Sim, 7 Sep 2026: "the error notification should not disappear after X
// seconds [...] only the error notification, the success notification still
// auto close it").
//
// `react-notifications` treats a timeOut of 0 as "never" — Notification.js only
// sets its setTimeout when `timeOut !== 0`, and 0 survives both
// NotificationManager's Object.assign default and React's defaultProps, which
// fill in for `undefined` only.
const AUTO_DISMISS_MS = 3000;
const NEVER_DISMISS = 0;

// THE "X", TOP RIGHT (Sim, 7 Sep 2026). A toast that never leaves needs a way
// out, and it needs to LOOK like it has one.
//
// `message` is a PropTypes.node, so the whole body is passed as elements rather
// than a string. React.createElement rather than JSX so this file stays .ts.
//
// NO onClick OF ITS OWN, deliberately: react-notifications puts the dismiss
// handler on the notification's outer div (Notification#handleClick), so a
// click here bubbles straight into it. That also means the toast keeps its
// existing click-anywhere-to-dismiss behaviour — if this button ever fails to
// render, an error toast is still closable rather than stuck on screen for
// good. This is a real, focusable, labelled control instead of a CSS glyph for
// the same reason.
//
// Positioned by `.notification-error .notification-close` in
// scss/react-notifications.css.
const withCloseButton = (message: string) =>
	React.createElement(
		React.Fragment,
		null,
		React.createElement("span", { className: "notification-text" }, message),
		React.createElement(
			"button",
			{ type: "button", className: "notification-close", "aria-label": "Close" },
			"×"
		)
	);

// A STALE ERROR GOES WHEN ANYTHING ELSE HAPPENS (Sim, 16-17 Sep 2026). An error
// toast waits to be dismissed, which is right while it is the last thing that
// happened — but once something else has happened, the red box is describing a
// moment that has passed, and it just sits there.
//
// 16 Sep: a SUCCESS clears it. 17 Sep: ANOTHER ERROR clears it too — *"When
// there is another error message, the previous error message should not be
// shown."* Sim had the sign-in screen showing "You do not have access to this
// application" from a refused SSO redirect stacked above "Password login is
// disabled for this account" from the attempt he had just made. Two red boxes,
// only the lower one current, and because neither auto-dismisses they pile up
// until they are closed one by one. ONE error on screen, always the latest.
//
// Nothing here reports a batch of failures as several toasts — the one place
// that could (Projects/Create) builds a single sentence — so replacing loses
// nothing. If that ever changes, join the messages rather than firing twice.
//
// WARNING is deliberately NOT one of the triggers. It auto-dismisses like info,
// but it still reports something being off, and it commonly accompanies the very
// operation that failed — clearing the error for it would throw away the detail
// while leaving the complaint.
//
// `listNotify` is the manager's live array and `remove` is its own removal path
// (filter by id, then emitChange), the same one the toast's click handler uses.
// The `.filter` snapshot is iterated rather than the live array, because each
// remove() reassigns it.
const dismissOpenErrors = () => {
	const open = (NotificationManager.listNotify || []).filter((notify: any) => notify.type === "error");
	open.forEach((notify: any) => NotificationManager.remove(notify));
};

export default class LocalNotification {
	private message: string;
	private title?: string;
	private type?: string;
	constructor(message: string, title = "tcbot", type: string = "info") {
		this.message = message;
		this.title = title;
		this.type = type;
		this.deliver();
	}

	deliver() {
		switch (this.type) {
			case "success":
				dismissOpenErrors();
				NotificationManager.success(this.message, this.title, AUTO_DISMISS_MS);
				break;
			case "warning":
				NotificationManager.warning(this.message, this.title, AUTO_DISMISS_MS);
				break;
			case "error":
				// Before, not after: the new toast must not be caught by its own
				// sweep. listNotify is the manager's live array.
				dismissOpenErrors();
				NotificationManager.error(withCloseButton(this.message), this.title, NEVER_DISMISS);
				break;
			default:
				dismissOpenErrors();
				NotificationManager.info(this.message, this.title, AUTO_DISMISS_MS);
				break;
		}
	}
}
